/**
 * Browser-tier silent token refresh.
 *
 * Closes the AWSDO-1316 scope gap: SCRUM-180 family delivered agent-tier
 * OAuth2 + RBAC infrastructure (SCRUM-181..186) including server-side
 * agent token refresh via the Agent Credential Management Service
 * (SCRUM-183), but never shipped a BROWSER-tier silent-refresh layer
 * for human users on long-lived monitoring tabs.
 *
 * Model:
 *  - JWT is delivered as an HttpOnly cookie (archon_jwt). JS cannot read
 *    it directly — so silent refresh works via:
 *      1) interceptor — wrap fetch, detect 401, POST /auth-api/auth/refresh,
 *         retry on success
 *      2) proactive timer — schedule refresh at (TTL × REFRESH_LEAD)
 *         seconds while the page has focus, before the cookie expires
 *  - Both layers compose: the interceptor is the safety net, the timer
 *    avoids the round-trip on the user's foreground action
 *
 * Backend contract (verified live on TPG 2026-05-13):
 *   POST /auth-api/auth/refresh
 *   - credentials: include (browser sends archon_jwt cookie automatically)
 *   - on success: 200 + new cookie set by Set-Cookie + JSON { expiresIn }
 *   - on failure: 401 (cookie invalid/expired) — caller clears session
 *
 * Concurrency: a single in-flight refresh promise is shared across
 * concurrent callers via the `pendingRefresh` singleton. Without this,
 * a burst of 401s would trigger N parallel refresh requests, each
 * potentially issuing a fresh JWT (the backend resets expiry every
 * call) — wasteful and creates a race on Set-Cookie ordering.
 */
const REFRESH_ENDPOINT = '/auth-api/auth/refresh';
/**
 * Singleton in-flight refresh promise. While set, all callers of
 * `refreshToken()` await this same promise instead of issuing new
 * requests.
 */
let pendingRefresh = null;
/**
 * POST /auth-api/auth/refresh — returns true if the cookie was rotated
 * successfully, false on any failure (network, 401, malformed response).
 *
 * Dedups concurrent calls: while one refresh is in flight, additional
 * callers share its result.
 */
export function refreshToken() {
    if (pendingRefresh)
        return pendingRefresh;
    pendingRefresh = (async () => {
        try {
            const response = await fetch(REFRESH_ENDPOINT, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            });
            return response.ok;
        }
        catch {
            // Network failure — treat as refresh failure; caller will logout.
            return false;
        }
        finally {
            pendingRefresh = null;
        }
    })();
    return pendingRefresh;
}
/**
 * Wrap any fetch-shaped function with silent-refresh behaviour: on a
 * 401 response, attempt a single refresh, then retry the original
 * request once. If the refresh fails or the retry still 401s, the
 * caller sees the 401 and is expected to clear session state.
 *
 * The retry is guarded so a request whose 401 was a real auth failure
 * (revoked user, wrong device fingerprint) does NOT loop — only one
 * retry per original call.
 */
export function withSilentRefresh(originalFetch) {
    return async (input, init) => {
        const response = await originalFetch(input, init);
        if (response.status !== 401)
            return response;
        const refreshed = await refreshToken();
        if (!refreshed)
            return response;
        // Retry once with a fresh cookie. Note: the cookie was rotated by
        // the backend's Set-Cookie header; browser handles cookie storage
        // transparently, the retried request will send the new cookie.
        return originalFetch(input, init);
    };
}
/**
 * Schedule a proactive refresh at (expSec - leadSec) — i.e. `leadSec`
 * seconds BEFORE the cookie expires. Returns a cancel function that
 * clears the timer if the caller (e.g. AppShell unmounts) wants to.
 *
 * Defaults:
 *  - leadSec: 300 (refresh 5 minutes before expiry)
 *  - if expSec is already in the past (clock skew, stale claims), the
 *    refresh fires immediately (setTimeout 0)
 *  - if the lead window would put the refresh in the past, fires
 *    immediately
 */
export function scheduleProactiveRefresh(expSec, leadSec = 300) {
    const nowMs = Date.now();
    const expMs = expSec * 1000;
    const leadMs = leadSec * 1000;
    const fireAtMs = Math.max(0, expMs - leadMs - nowMs);
    const handle = window.setTimeout(() => {
        void refreshToken();
    }, fireAtMs);
    return () => window.clearTimeout(handle);
}
//# sourceMappingURL=silentRefresh.js.map